// client/src/components/LegalDocument.jsx
import React, { useState, useEffect } from 'react';
import { FileText, Calendar, Loader2, AlertCircle, Cookie } from 'lucide-react';
import SEOLayout from './layout/SEOLayout';

const LegalDocument = ({ documentType, title, description }) => {
    const [content, setContent] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastModified, setLastModified] = useState(null);

    useEffect(() => {
        const loadDocument = async () => {
            setLoading(true);
            setError(null);

            try {
                const response = await fetch(`/legal/${documentType}.md`);

                if (!response.ok) {
                    throw new Error(`Failed to load ${documentType} document`);
                }

                const markdownText = await response.text();

                // Extract last modified date from markdown if present
                const lastModifiedMatch = markdownText.match(/Last Modified:\s*(.+)/i);
                if (lastModifiedMatch) {
                    setLastModified(lastModifiedMatch[1].trim());
                }

                // Convert markdown to HTML (basic conversion)
                const htmlContent = convertMarkdownToHtml(markdownText);
                setContent(htmlContent);

            } catch (err) {
                console.error(`Error loading ${documentType} document:`, err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        loadDocument();
    }, [documentType]);

    // Basic markdown to HTML conversion
    const convertMarkdownToHtml = (markdown) => {
        let html = markdown;

        // Headers
        html = html.replace(/^# (.*$)/gim, '<h1 class="text-3xl font-bold text-gray-900 mb-6">$1</h1>');
        html = html.replace(/^## (.*$)/gim, '<h2 class="text-2xl font-semibold text-gray-900 mb-4 mt-8">$1</h2>');
        html = html.replace(/^### (.*$)/gim, '<h3 class="text-xl font-medium text-gray-900 mb-3 mt-6">$1</h3>');
        html = html.replace(/^#### (.*$)/gim, '<h4 class="text-lg font-medium text-gray-900 mb-2 mt-4">$1</h4>');

        // Bold and italic
        html = html.replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold text-gray-900">$1</strong>');
        html = html.replace(/\*(.*?)\*/g, '<em class="italic">$1</em>');

        // Links
        html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-blue-600 hover:text-blue-700 underline" target="_blank" rel="noopener noreferrer">$1</a>');

        // Email links
        html = html.replace(/([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g, '<a href="mailto:$1" class="text-blue-600 hover:text-blue-700 underline">$1</a>');

        // Lists
        html = html.replace(/^\* (.+$)/gim, '<li class="mb-2">$1</li>');
        html = html.replace(/^- (.+$)/gim, '<li class="mb-2">$1</li>');

        // Wrap consecutive list items in ul tags
        html = html.replace(/(<li[^>]*>.*<\/li>\s*)+/gs, (match) => {
            return `<ul class="list-disc list-inside mb-4 space-y-2 ml-4">${match}</ul>`;
        });

        // Paragraphs
        html = html.replace(/^(?!<[hl]|<ul|<li|<strong)([^\r\n]+)$/gim, '<p class="mb-4 leading-relaxed text-gray-700">$1</p>');

        // Line breaks
        // html = html.replace(/\n/g, '<br />');

        return html;
    };

    // Cookie consent revoke function for privacy policy
    const handleCookieRevoke = () => {
        if (window.revokeCookieConsent) {
            window.revokeCookieConsent();
        } else {
            alert('Cookie consent management is not available.');
        }
    };

    if (loading) {
        return (
            <SEOLayout title={title} description={description}>
                <div className="min-h-screen flex items-center justify-center bg-gray-50">
                    <div className="text-center">
                        <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
                        <p className="text-gray-600">Loading {title}...</p>
                    </div>
                </div>
            </SEOLayout>
        );
    }

    if (error) {
        return (
            <SEOLayout title={title} description={description}>
                <div className="min-h-screen flex items-center justify-center bg-gray-50">
                    <div className="text-center max-w-md">
                        <AlertCircle className="h-12 w-12 text-red-600 mx-auto mb-4" />
                        <h1 className="text-2xl font-bold text-gray-900 mb-4">Document Not Available</h1>
                        <p className="text-gray-600 mb-6">{error}</p>
                        <a
                            href="/"
                            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
                        >
                            Return Home
                        </a>
                    </div>
                </div>
            </SEOLayout>
        );
    }

    return (
        <SEOLayout title={title} description={description}>
            <div className="bg-gray-50 min-h-screen">
                {/* Header */}
                <div className="bg-white shadow-sm border-b border-gray-200">
                    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                        <div className="flex items-center space-x-3 mb-4">
                            <FileText className="h-8 w-8 text-blue-600" />
                            <h1 className="text-3xl font-bold text-gray-900">{title}</h1>
                        </div>
                        <p className="text-lg text-gray-600 mb-4">{description}</p>
                        {lastModified && (
                            <div className="flex items-center space-x-2 text-sm text-gray-500">
                                <Calendar className="h-4 w-4" />
                                <span>Last Modified: {lastModified}</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Content */}
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
                        {/* Special cookie management section for privacy policy */}
                        {documentType === 'privacy' && (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
                                <div className="flex items-start space-x-3">
                                    <Cookie className="h-6 w-6 text-blue-600 mt-1 flex-shrink-0" />
                                    <div className="flex-1">
                                        <h3 className="text-lg font-medium text-blue-900 mb-2">
                                            Cookie Preferences
                                        </h3>
                                        <p className="text-blue-700 mb-4">
                                            You can manage your cookie preferences at any time. Click the button below to review and update your choices.
                                        </p>
                                        <button
                                            onClick={handleCookieRevoke}
                                            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200"
                                        >
                                            <Cookie className="h-4 w-4 mr-2" />
                                            Manage Cookie Preferences
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Document content */}
                        <div
                            className="prose prose-lg max-w-none legal-content"
                            dangerouslySetInnerHTML={{ __html: content }}
                        />

                        {/* Contact information footer */}
                        <div className="mt-12 pt-8 border-t border-gray-200">
                            <div className="bg-gray-50 rounded-lg p-6">
                                <h3 className="text-lg font-medium text-gray-900 mb-3">
                                    Questions or Concerns?
                                </h3>
                                <p className="text-gray-600 mb-4">
                                    If you have any questions about this {title.toLowerCase()}, please don't hesitate to contact us.
                                </p>
                                <div className="space-y-2 text-sm">
                                    <p className="text-gray-700">
                                        <strong>Email:</strong>{' '}
                                        <a
                                            href="mailto:solutions@baraqu.com"
                                            className="text-blue-600 hover:text-blue-700 underline"
                                        >
                                            solutions@baraqu.com
                                        </a>
                                    </p>
                                    <p className="text-gray-700">
                                        <strong>Phone:</strong>{' '}
                                        <a
                                            href="tel:+13105702674"
                                            className="text-blue-600 hover:text-blue-700 underline"
                                        >
                                            +1 (310) 570-2674
                                        </a>
                                    </p>
                                    <p className="text-gray-700">
                                        <strong>Address:</strong> Los Angeles, California
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Custom styles for legal content */}
            <style jsx>{`
                .legal-content h1 {
                    border-bottom: 2px solid #e5e7eb;
                    padding-bottom: 0.5rem;
                }
                
                .legal-content h2 {
                    color: #1f2937;
                    border-left: 4px solid #3b82f6;
                    padding-left: 1rem;
                    margin-left: -1rem;
                    background-color: #f8fafc;
                    padding-top: 0.5rem;
                    padding-bottom: 0.5rem;
                }
                
                .legal-content h3 {
                    color: #374151;
                }
                
                .legal-content p {
                    line-height: 1.7;
                }
                
                .legal-content ul {
                    margin-left: 1rem;
                }
                
                .legal-content li {
                    padding-left: 0.5rem;
                }
                
                .legal-content strong {
                    color: #111827;
                }
                
                .legal-content a {
                    text-decoration: underline;
                    text-decoration-thickness: 1px;
                    text-underline-offset: 2px;
                }
                
                .legal-content a:hover {
                    text-decoration-thickness: 2px;
                }
            `}</style>
        </SEOLayout>
    );
};

export default LegalDocument;